import { findCountColumn, looksLikeHeader, rowPeople } from "./attendees";
import {
  ROSTER_ID_COLUMN_MIN_ROWS,
  ROSTER_ID_COLUMN_SHARE,
  ROSTER_ID_COLUMN_VALUES,
  ROSTER_MAX_VALUES_PER_COLUMN,
  ROSTER_SAMPLE_ROWS,
} from "./constants";

/** An attendee list as the assistant reads it: how many people, and the text it is given. */
export interface Roster {
  fileName: string;
  /** People in the list, when known before the model reads it (a sheet's rows; not a document's). */
  people?: number;
  text: string;
}

/**
 * The text the assistant reads for an attached list, file name first.
 * @param roster - The attached list.
 * @returns The roster block's contents.
 * @throws Never.
 */
export const rosterBlock = (roster: Roster): string => `File: ${roster.fileName}\n\n${roster.text}`;

/** One cell made safe for a comma-separated sample row. */
const csvCell = (value: string): string => (/[",\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value);

/**
 * A sheet reduced to what the assistant needs to find home cities: the header, a few rows for
 * shape, and each column's distinct values with how many people hold them, most common first. The
 * counts are exact, so the assistant adds them up rather than counting rows itself, and a long
 * list stays small. A "Count" or "Travelers" column weights each row by its number, as the local
 * parser reads it. Columns of mostly distinct values (names, emails) are cut short.
 * @param rows - Cell grid from `readAttendeeRows`.
 * @returns How many people the sheet lists, and the text.
 * @throws Never.
 */
export const describeSheetRoster = (rows: readonly string[][]): { people: number; text: string } => {
  const hasHeader = looksLikeHeader(rows);
  const data = hasHeader ? rows.slice(1) : rows;
  const width = Math.max(0, ...rows.map((row) => row.length));
  const countColumn = hasHeader ? findCountColumn(rows[0]) : -1;
  const weights = data.map((row) => rowPeople(row, countColumn));
  const people = weights.reduce((sum, weight) => sum + weight, 0);
  const label = (index: number) => (hasHeader ? rows[0][index]?.trim() : "") || `Column ${index + 1}`;

  const columns: string[] = [];
  for (let index = 0; index < width; index += 1) {
    if (index === countColumn) continue;
    const counts = new Map<string, number>();
    let blank = 0;
    data.forEach((row, at) => {
      const value = (row[index] ?? "").trim();
      if (value) counts.set(value, (counts.get(value) ?? 0) + weights[at]);
      else blank += weights[at];
    });
    if (counts.size === 0) continue;
    const sorted = [...counts.entries()].sort((a, b) => b[1] - a[1]);
    // A column where nearly every row differs (names, emails, notes) is one person per value, not a grouping.
    const perPerson = data.length >= ROSTER_ID_COLUMN_MIN_ROWS && counts.size >= data.length * ROSTER_ID_COLUMN_SHARE;
    const shown = sorted
      .slice(0, perPerson ? ROSTER_ID_COLUMN_VALUES : ROSTER_MAX_VALUES_PER_COLUMN)
      .map(([value, count]) => `${value} (${count})`);
    const more = sorted.length - shown.length;
    columns.push(
      `${label(index)}: ${shown.join("; ")}${more > 0 ? `; and ${more} more distinct values` : ""}${blank ? `; blank (${blank})` : ""}`
    );
  }

  const sample = data.slice(0, ROSTER_SAMPLE_ROWS).map((row) => row.map(csvCell).join(","));
  const text = [
    countColumn === -1
      ? `${people} people, one per row.`
      : `${people} people in ${data.length} rows; the "${label(countColumn)}" column says how many people each row stands for.`,
    hasHeader ? `Header: ${rows[0].map(csvCell).join(",")}` : "No header row.",
    `First rows:\n${sample.join("\n")}`,
    `Each column's values with how many people hold them:\n${columns.join("\n")}`,
  ].join("\n\n");
  return { people, text };
};
